import Logger from '../../logging/logger';

export enum TWCEventName {
    Call = 'call',
    CallV2 = 'call_v2',
    RevealNumber = 'reveal_telephone',
    RevealNumberV2 = 'reveal',
    VisibilityLog = 'visibility_log',
    View = 'view',
    List = 'list',
    Save = 'save',
    Enquiry = 'enquiry',
    ImageInteraction = 'image_interaction',
}

export const validEventNames: Array<TWCEventName> = [
    TWCEventName.Call,
    TWCEventName.CallV2,
    TWCEventName.RevealNumber,
    TWCEventName.RevealNumberV2,
    TWCEventName.VisibilityLog,
    TWCEventName.View,
    TWCEventName.List,
    TWCEventName.Save,
    TWCEventName.Enquiry,
    TWCEventName.ImageInteraction,
];

type TWCEventParameter = string | number | bigint | boolean | object | undefined;

export interface TWCEventParameters {
    [ key: string ]: TWCEventParameter;
}

export interface TWCEvent<T extends TWCEventParameters> extends BaseEvent<T> {
    eventName: TWCEventName;
}

type BaseEvent<T extends TWCEventParameters> ={
    [ key in TWCEventName ]?: T;
}

export interface CommonEventParameters extends TWCEventParameters {
    propertyID?: number;
    businessID?: number;
    clientID?: string;
    locationID?: number;
    offer?: string;
    ref?: string;
    price?: number;
    beds?: number;
    type?: string;
    buildSqm?: number;
    productVariant?: string;
}

export interface TWCEnquiryEvent extends CommonEventParameters {
    enquiryID: number;
    enquiryRef: string;
}

export interface TWCSaveEvent extends CommonEventParameters {}

export interface TWCViewEvent extends CommonEventParameters {
    imgs: number;
    aiImgs: number;
}

export interface TWCListEvent extends CommonEventParameters {
    listID?: string;
}

export interface TWCImageInteractionEvent extends CommonEventParameters {
    action: string;
    index: number;
}

export interface TWCVisibilityLogEvent extends CommonEventParameters {
    id?: string;
    group: string;
    index?: number;
    duration: number;
}

export type TWCCallEvent = {
    destination: string;
    originCountry: string;
    origins: Array<string>;
    itemID: number;
}

export interface TWCCallEventV2 extends CommonEventParameters {
    destination: string;
    originCountry: string;
    origins: Array<string>;
}

export type TWCRevealTelephoneEvent = {
    destination: string;
    itemID: number;
    itemType: string;
    type: string;
    clientID: string;
}

export interface TWCRevealTelephoneEventV2 extends CommonEventParameters {
    destination: string;
    destinationType: Array<string>;
}

export function triggerTWCEvent<T extends TWCEventParameters>( eventName: TWCEventName, data: T ): void {
    const event = {
        eventName: eventName,
        [ eventName ]: data,
    };

    const err: Error | null = validateEvent<T>( event );

    if ( err !== null ) {
        throw new Error( `event ${event.eventName} invalid, ${err.message}` );
    }

    Logger.info( `TWC analytics event: ${eventName}`, event );

    if ( !window.twc ) {
        throw new Error( 'twc undefined' );
    }

    window.twc.track( `web_${eventName}`, event );
}

type ValidationFunc = ( event: any ) => Error | null;

const validators: Record<string, ValidationFunc> = {};

export function validateEvent<T extends TWCEventParameters>( event: TWCEvent<T> ): Error | null {
    if ( !validEventNames.includes( event.eventName ) ) {
        return new Error( `unknown event "${event.eventName}"` );
    }

    const eventObj = event[ event.eventName ] as TWCEventParameters;

    if ( !eventObj ) {
        return new Error( `no event obj found for event ${event.eventName}` );
    }

    const validator: ValidationFunc = validators[ event.eventName ];

    if ( validator ) {
        const err = validator( eventObj );

        if ( err !== null ) {
            return err;
        }
    }

    return null;
}

export function getCommonElementParameters( element: HTMLElement ): TWCEventParameters {
    if ( element.dataset.baseTwcAnalyticEventParameters ) {
        return JSON.parse( element.dataset.baseTwcAnalyticEventParameters || '{}' ) as TWCEventParameters;
    }

    const parentElementWithSharedParams = element.closest<HTMLElement>( '[data-base-twc-analytic-event-parameters]' );

    if ( parentElementWithSharedParams && parentElementWithSharedParams.dataset.baseTwcAnalyticEventParameters ) {
        return JSON.parse( parentElementWithSharedParams.dataset.baseTwcAnalyticEventParameters || '{}' ) as TWCEventParameters;
    }

    return {} as TWCEventParameters;
}
